import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  Flex,
  List,
  ListItem,
  ListIcon,
  Image,
} from "@chakra-ui/react";
import {
  FaBatteryFull,
  FaBolt,
  FaCheckCircle,
  FaArrowRight,
  FaNetworkWired,
  FaSolarPanel,
  FaHome,
  FaShieldAlt,
  FaDollarSign,
  FaLeaf,
  FaWifi,
  FaPhone,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

const SolarServicesBattery: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services/battery"
        fallbackTitle="Solar Batteries, Virtual Power Plants & Off Grid Systems | One Solar Australia"
        fallbackDescription="Store your solar energy, join a Virtual Power Plant, and earn up to 45c per kW feed-in. One Solar Australia — Queensland's battery storage specialists."
      />
      <NavbarWithCallToAction />

      {/* ── Hero ── */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 32 }}
        backgroundImage="url('/assets/solar-assets/asset%2037.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.62)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 2px 8px rgba(198,146,20,0.5)"
            >
              One Solar Australia
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="4xl" lineHeight="1.15">
              Virtual Power Plants, Solar Batteries &amp; Off Grid Systems
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="3xl" lineHeight="1.8">
              Store your solar energy, maximise your returns, and take control of your power — day and night.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={8}
                transition="all 0.2s"
              >
                Get a Free Quote
              </Button>
              <Button
                as={RouterLink}
                to="/solar/contact"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                px={8}
                transition="all 0.2s"
              >
                <HStack spacing={2}>
                  <FaPhone />
                  <Text>1300 One Solar</Text>
                </HStack>
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ── Key Stats — parallax ── */}
      <Box
        position="relative"
        color="white"
        py={20}
        backgroundImage="url('/assets/solar-assets/asset%2020.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.70)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" textShadow="0 2px 4px rgba(0,0,0,0.4)">
              Why Batteries Change Everything
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} w="full" pt={4}>
              {[
                { value: "45c", label: "Feed-in Rate per kWh" },
                { value: "19c", label: "Peak Usage Rate" },
                { value: "85c", label: "Supply Charge" },
                { value: "24/7", label: "Clean Power Available" },
              ].map((s) => (
                <VStack key={s.label} spacing={2}>
                  <Heading size="2xl" color="white" textShadow="0 2px 8px rgba(0,0,0,0.4)">{s.value}</Heading>
                  <Text fontSize="md" color="rgba(255,255,255,0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)" textAlign="center">{s.label}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* ── Why You Need a Solar Battery — split ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "300px", md: "460px" }} position="relative" boxShadow="2xl">
            <Box
              as="img"
              src="https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d?w=900&q=80"
              alt="Home solar battery storage system"
              w="full" h="full"
              objectFit="cover"
            />
            <Box position="absolute" bottom={0} left={0} right={0} p={5} bgGradient={`linear(to-t, ${NAVY}DD, transparent)`}>
              <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                Store energy. Use it on your terms.
              </Text>
            </Box>
          </Box>
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Why You Need a Solar Battery
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Your Solar Panels Work 9 to 5. Your Battery Works 24/7.
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              A solar battery helps Australian homeowners store unused solar energy generated during the day so it can be used later at night, during peak electricity pricing periods, or during power outages.
            </Text>
            <Text color="gray.600" fontSize="md" lineHeight="1.8">
              Without a battery, any solar energy you don't use immediately goes back to the grid — often at a low feed-in rate. A battery lets you keep that energy and use it when electricity prices are at their highest, or simply keep the lights on when the grid goes down.
            </Text>
            <SimpleGrid columns={2} spacing={4} w="full" pt={2}>
              {[
                { icon: FaBolt, label: "Use Power at Night" },
                { icon: FaShieldAlt, label: "Blackout Protection" },
                { icon: FaDollarSign, label: "Avoid Peak Rates" },
                { icon: FaLeaf, label: "100% Clean Energy" },
              ].map((f) => (
                <HStack key={f.label} spacing={3} p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.100">
                  <Icon as={f.icon} color={SOLAR_GOLD} boxSize={5} flexShrink={0} />
                  <Text fontSize="sm" fontWeight="600" color={NAVY}>{f.label}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* ── How It Works ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              How It Works
            </Badge>
            <Heading size="xl" color={NAVY}>Simple Solar Energy Flow</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              From generation to storage to use — your battery system works automatically, around the clock.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                step: "01",
                icon: FaSolarPanel,
                title: "Solar Generation",
                body: "Your solar panels generate clean electricity during daylight hours — powering your home directly and charging your battery simultaneously.",
              },
              {
                step: "02",
                icon: FaBatteryFull,
                title: "Energy Storage",
                body: "Excess solar energy that would otherwise be exported at low feed-in rates is stored in your battery, ready for when you need it most.",
              },
              {
                step: "03",
                icon: FaBolt,
                title: "Power on Demand",
                body: "At night, on cloudy days, or during grid outages, your battery delivers clean stored energy automatically — no manual switching required.",
              },
            ].map((item) => (
              <Box key={item.step} bg="white" borderRadius="2xl" p={8} boxShadow="md" border="1px solid" borderColor="gray.100" _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }} transition="all 0.3s" position="relative">
                <Box
                  w={12} h={12}
                  bg={SOLAR_GOLD}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="black"
                  fontSize="lg"
                  color="white"
                  mb={5}
                  boxShadow="0 2px 8px rgba(198,146,20,0.4)"
                >
                  {item.step}
                </Box>
                <Icon as={item.icon} boxSize={9} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" color={NAVY} mb={3}>{item.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.8">{item.body}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Feed-in Rates Highlight ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Maximise Your Returns
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Get the Best Feed-In Rates for Your Excess Power
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              Certain providers offer up to <Text as="span" fontWeight="800" color={SOLAR_GOLD}>45 cents per kWh</Text> — more than twice the going rate from other providers. With a battery and the right energy plan, your solar system becomes a genuine income source.
            </Text>
            <Box w="full" bg="gray.50" borderRadius="2xl" p={6} border="1px solid" borderColor="gray.200">
              <VStack spacing={4} align="stretch">
                {[
                  { label: "Best Feed-in Rate", value: "Up to 45c/kWh", highlight: true },
                  { label: "Peak Usage Rate", value: "As low as 19c/kWh", highlight: false },
                  { label: "Supply Charge", value: "Just over 85c/day", highlight: false },
                ].map((row) => (
                  <Flex key={row.label} justify="space-between" align="center" p={4} bg={row.highlight ? NAVY : "white"} borderRadius="xl" border="1px solid" borderColor={row.highlight ? NAVY : "gray.200"}>
                    <Text fontWeight="600" color={row.highlight ? "white" : "gray.700"} fontSize="sm">{row.label}</Text>
                    <Text fontWeight="800" color={row.highlight ? SOLAR_GOLD : NAVY} fontSize="lg">{row.value}</Text>
                  </Flex>
                ))}
              </VStack>
            </Box>
            <Text color="gray.500" fontSize="xs">
              * Rates vary by provider and location. Our Energy Advisors will identify the best plan for your situation.
            </Text>
            <Button
              as={RouterLink}
              to="/solar/contact"
              bg={SOLAR_GOLD}
              color="white"
              size="lg"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: SOLAR_GOLD_HOVER }}
              transition="all 0.2s"
            >
              Talk to an Energy Advisor
            </Button>
          </VStack>
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "280px", md: "420px" }} boxShadow="2xl">
            <Box
              as="img"
              src="/assets/solar-assets/asset%2013.jpeg"
              alt="Energy advisor consulting with homeowners"
              w="full" h="full"
              objectFit="cover"
            />
          </Box>
        </SimpleGrid>
      </Container>

      {/* ── What is a VPP ── */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: `rgba(20, 28, 73, 0.88)`,
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                Virtual Power Plant
              </Badge>
              <Heading size="xl" lineHeight="1.2">
                What is a Virtual Power Plant (VPP)?
              </Heading>
              <Text color="gray.300" fontSize="lg" lineHeight="1.8">
                A virtual power plant works remotely to combine several independent energy resources from disparate locations into a network that provides reliable power 24 hours a day.
              </Text>
              <Text color="gray.300" fontSize="md" lineHeight="1.8">
                Using software-based technology and the smart grid, your home battery becomes part of a larger network. In exchange, your energy provider rewards you with some of the highest feed-in rates available in Australia — up to 45c per kWh.
              </Text>
              <List spacing={3} pt={2}>
                {[
                  "Earn higher feed-in rates by sharing stored energy",
                  "Your battery is managed automatically via smart software",
                  "Help stabilise the grid during peak demand events",
                  "Retain full control — your battery stays charged for your home first",
                  "No additional hardware required in most cases",
                ].map((pt) => (
                  <ListItem key={pt} display="flex" alignItems="flex-start" gap={3}>
                    <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} mt="3px" flexShrink={0} boxSize={4} />
                    <Text fontSize="sm" color="gray.300" lineHeight="1.7">{pt}</Text>
                  </ListItem>
                ))}
              </List>
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                transition="all 0.2s"
              >
                Join a VPP Program
              </Button>
            </VStack>
            <SimpleGrid columns={2} spacing={5}>
              {[
                { icon: FaNetworkWired, title: "Network Power", body: "Your battery connects to a virtual network of hundreds of homes providing grid-scale storage." },
                { icon: FaDollarSign, title: "Earn More", body: "VPP participants earn up to 45c/kWh — more than double typical feed-in tariffs." },
                { icon: FaWifi, title: "Smart Control", body: "Software automatically manages exports and charges, always keeping your home's needs first." },
                { icon: FaShieldAlt, title: "Grid Stability", body: "VPP networks prevent blackouts by balancing supply and demand across the grid in real time." },
              ].map((f) => (
                <Box key={f.title} bg="whiteAlpha.100" borderRadius="2xl" p={6} border="1px solid" borderColor="whiteAlpha.200" _hover={{ bg: "whiteAlpha.200" }} transition="all 0.3s">
                  <Icon as={f.icon} boxSize={8} color={SOLAR_GOLD} mb={3} />
                  <Heading size="sm" color="white" mb={2}>{f.title}</Heading>
                  <Text fontSize="xs" color="gray.400" lineHeight="1.7">{f.body}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Off-Grid Systems ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Off-Grid Systems
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Complete Energy Independence
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              For homeowners who want to go completely grid-free, our off-grid solar systems combine large battery banks with appropriately sized solar arrays to deliver 100% self-sufficient power — no electricity bills, ever.
            </Text>
            <List spacing={3}>
              {[
                "Designed for properties in remote or regional Queensland",
                "Correctly sized battery bank for year-round reliability",
                "Backup generator integration available",
                "Full system monitoring and remote management",
                "Ideal for new builds, rural properties, and lifestyle blocks",
              ].map((pt) => (
                <ListItem key={pt} display="flex" alignItems="flex-start" gap={3}>
                  <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} mt="3px" flexShrink={0} boxSize={4} />
                  <Text fontSize="sm" color="gray.700" lineHeight="1.7">{pt}</Text>
                </ListItem>
              ))}
            </List>
            <Button
              as={RouterLink}
              to="/solar/contact"
              bg={NAVY}
              color="white"
              size="lg"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#0d1235" }}
              transition="all 0.2s"
            >
              Discuss Off-Grid Options
            </Button>
          </VStack>
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "280px", md: "420px" }} position="relative" boxShadow="2xl">
            <Box
              as="img"
              src="/assets/solar-assets/asset%2033.jpeg"
              alt="Professional solar installer working on system"
              w="full" h="full"
              objectFit="cover"
            />
            <Box position="absolute" bottom={0} left={0} right={0} p={5} bgGradient={`linear(to-t, ${NAVY}CC, transparent)`}>
              <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                CEC Accredited — Lic. 88686
              </Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* ── Battery Benefits Grid ── */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>Why Add Battery Storage?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              A battery transforms your solar system from a daytime energy source into a true 24-hour power station.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {[
              { icon: FaBolt, title: "24/7 Solar Power", body: "Use stored clean energy at night and on overcast days — no longer limited to daylight hours." },
              { icon: FaHome, title: "Blackout Protection", body: "Your battery switches to backup mode automatically during grid outages — lights stay on." },
              { icon: FaDollarSign, title: "Peak Rate Avoidance", body: "Discharge your battery during expensive peak periods and recharge overnight at off-peak rates." },
              { icon: FaLeaf, title: "Maximise Solar ROI", body: "Use 80%+ of your solar generation rather than exporting it at low rates. Faster payback period." },
            ].map((b) => (
              <VStack key={b.title} p={7} bg="white" borderRadius="xl" boxShadow="md" spacing={4} align="flex-start" border="1px solid" borderColor="gray.100" _hover={{ borderColor: SOLAR_GOLD, boxShadow: "lg" }} transition="all 0.3s">
                <Icon as={b.icon} boxSize={9} color={SOLAR_GOLD} />
                <Heading size="sm" color={NAVY}>{b.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.7">{b.body}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Battery Brand Partners ── */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} mb={10} textAlign="center">
          <Heading size="lg" color={NAVY}>Battery Brands We Install</Heading>
          <Text color="gray.600" maxW="xl">
            We only install battery systems from manufacturers with proven performance and long-term warranty support.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6} alignItems="center">
          {[
            { img: "/assets/solar-assets/asset%2026.png", alt: "LG Chem" },
            { img: "/assets/solar-assets/asset%2022.png", alt: "Sungrow" },
            { img: "/assets/solar-assets/asset%2023.png", alt: "Solar Analytics" },
            { img: "/assets/solar-assets/asset%2024.png", alt: "Risen Solar Technology" },
            { img: "/assets/solar-assets/asset%2025.png", alt: "Q Cells" },
            { img: "/assets/solar-assets/asset%2027.png", alt: "Fronius" },
          ].map((b) => (
            <Box
              key={b.alt}
              p={4}
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ borderColor: SOLAR_GOLD, boxShadow: "md" }}
              transition="all 0.3s"
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="80px"
            >
              <Image src={b.img} alt={b.alt} maxH="44px" maxW="130px" objectFit="contain" />
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Savings Calculator CTA ── */}
      <Box bg={NAVY} py={20}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center" color="white">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              See Your Savings
            </Badge>
            <Heading size="xl">How Much Can You Save With Solar?</Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              Use our free savings calculator to see your estimated energy savings, payback period, and earnings from a VPP program — tailored to your property and usage.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/calculator"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={8}
                transition="all 0.2s"
              >
                Calculate My Savings
              </Button>
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                px={8}
                transition="all 0.2s"
              >
                Get Free Quote
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarServicesBattery;
